// AssignVouchers.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  TextInput,
  SafeAreaView,
  Linking,
  Modal,
  Alert,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const FETCH_CREATORS_URL =
  'https://collabbayadmin.com/APIControllers/FetchCreatorsForVouchers.php';

const INSERT_VOUCHER_URL =
  'https://collabbayadmin.com/APIControllers/InsertVoucherStock.php';

const IMAGE_BASE = 'https://collabbayadmin.com';
const GIF_URL = 'https://collabbayadmin.com/VoucherExplain_CB_V1_GIF.gif';

const DEBUG_VOUCHERS = true;

const normaliseImageUri = (image) => {
  if (!image) return null;

  const trimmed = String(image).trim();

  if (!trimmed) return null;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `${IMAGE_BASE}${trimmed}`;
  }

  return `${IMAGE_BASE}/${trimmed}`;
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString('en-GB') : '0';
};

const formatMoney = (value) => {
  const n = Number(value || 0);

  if (!Number.isFinite(n)) {
    return '0';
  }

  if (Number.isInteger(n)) {
    return n.toLocaleString('en-GB');
  }

  return n.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDaysAgo = (daysAgo) => {
  if (daysAgo === null || daysAgo === undefined || daysAgo === '') {
    return 'Content delivered';
  }

  const n = Number(daysAgo);

  if (!Number.isFinite(n)) {
    return 'Content delivered';
  }

  if (n <= 0) {
    return 'Delivered today';
  }

  if (n === 1) {
    return 'Delivered 1 day ago';
  }

  return `Delivered ${n} days ago`;
};

const formatFollowersLabel = (followers) => {
  if (!followers) return 'Creator audience';

  const raw = String(followers).trim();

  if (!raw) return 'Creator audience';

  return `${raw} followers`;
};

const openInSameTab = (url) => {
  if (typeof window !== 'undefined') {
    window.location.href = url;
  } else {
    Linking.openURL(url);
  }
};

const showVoucherAlert = (title, message) => {
  const finalMessage = `${title}\n\n${message}`;

  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    window.alert(finalMessage);
    return;
  }

  Alert.alert(title, message);
};

export default function AssignVouchers({ navigation }) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const isWideHero = width >= 1120;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const [brandName, setBrandName] = useState('');
  const [creators, setCreators] = useState([]);

  const [summary, setSummary] = useState({
    EligibleCreators: 0,
    VoucherStockRows: 0,
    TotalStockAssigned: 0,
    TotalRedeemed: 0,
    TotalRemaining: 0,
    LiveCreators: 0,
  });

  const [selectedCreator, setSelectedCreator] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  const [Stock, setStock] = useState('');
  const [OnSpend, setOnSpend] = useState('');
  const [Discount, setDiscount] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [infoModalVisible, setInfoModalVisible] = useState(false);

  const [debugMessages, setDebugMessages] = useState([]);
  const [expandedVoucherKey, setExpandedVoucherKey] = useState(null);

  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none');
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const mounted = useRef(false);

  const addDebug = (label, data = null) => {
    if (!DEBUG_VOUCHERS) return;

    const time = new Date().toLocaleTimeString();

    let dataString = '';

    if (data !== null && data !== undefined) {
      try {
        dataString =
          typeof data === 'string' ? data : JSON.stringify(data, null, 0);
      } catch (e) {
        dataString = String(data);
      }
    }

    const message = dataString
      ? `${time} — ${label}: ${dataString}`
      : `${time} — ${label}`;

    console.log('[VoucherDebug]', label, data || '');

    setDebugMessages((prev) => [message, ...prev].slice(0, 10));
  };

  const getCreatorKey = (creator) => {
    const collabID = creator?.CollabID ? String(creator.CollabID).trim() : '';
    const promoter = creator?.Promoter ? String(creator.Promoter).trim() : '';

    return `${collabID}-${promoter}`;
  };

  const fetchAll = async () => {
    const cacheBuster = Date.now();

    addDebug('Fetch dashboard started');

    const response = await fetch(`${FETCH_CREATORS_URL}?d=${cacheBuster}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawText = await response.text();

    addDebug('Fetch raw response', rawText.slice(0, 300));

    let data;

    try {
      data = JSON.parse(rawText);
    } catch (jsonError) {
      addDebug('Fetch JSON parse failed', rawText.slice(0, 300));
      throw new Error(`Fetch PHP returned invalid JSON: ${rawText.slice(0, 160)}`);
    }

    if (!response.ok || !data.success) {
      addDebug('Fetch failed parsed response', data);
      throw new Error(data.message || data.debug || `Fetch HTTP ${response.status}`);
    }

    const safeCreators = Array.isArray(data.creators) ? data.creators : [];

    setBrandName(data.BrandName || '');
    setCreators(safeCreators);
    setSummary(
      data.summary || {
        EligibleCreators: 0,
        VoucherStockRows: 0,
        TotalStockAssigned: 0,
        TotalRedeemed: 0,
        TotalRemaining: 0,
        LiveCreators: 0,
      }
    );
    setErr('');

    addDebug('Fetch dashboard success', {
      creators: safeCreators.length,
      BrandName: data.BrandName || '',
      TotalStockAssignedFromPHP: data?.summary?.TotalStockAssigned || 0,
    });
  };

  useEffect(() => {
    mounted.current = true;

    (async () => {
      try {
        await fetchAll();
      } catch (e) {
        console.error('Fetch voucher creators error:', e);
        addDebug('Initial fetch error', e.message);
        setErr(e.message || 'Could not load voucher dashboard right now.');
        setCreators([]);
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted.current = false;
    };
  }, []);

  const onRefresh = async () => {
    try {
      addDebug('Manual refresh clicked');
      setRefreshing(true);
      await fetchAll();
    } catch (e) {
      console.error('Refresh failed:', e);
      addDebug('Refresh failed', e.message);
      setErr(e.message || 'Refresh failed. Try again.');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => setUserData(data || {}))
      .catch((error) =>
        console.error('Error fetching session user data:', error)
      );
  }, []);

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';

        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((error) => {
        console.error('Error fetching auth state:', error);
        setIsAuthed(false);
        setAuthMode('none');
      })
      .finally(() => setAuthChecked(true));
  }, []);

  const creatorsWithVouchers = useMemo(() => {
    return creators
      .filter((creator) => Number(creator.StockAssigned || 0) > 0)
      .sort((a, b) => Number(b.StockAssigned || 0) - Number(a.StockAssigned || 0));
  }, [creators]);

  const computedTotalStockAssigned = useMemo(() => {
    return creators.reduce((total, creator) => {
      return total + Number(creator.StockAssigned || 0);
    }, 0);
  }, [creators]);

  const computedTotalRedeemed = useMemo(() => {
    return creators.reduce((total, creator) => {
      return total + Number(creator.Redeemed || 0);
    }, 0);
  }, [creators]);

  const computedTotalRemaining = useMemo(() => {
    return creators.reduce((total, creator) => {
      const rowRemaining = Number(creator.Remaining || 0);

      if (Number.isFinite(rowRemaining)) {
        return total + rowRemaining;
      }

      return total;
    }, 0);
  }, [creators]);

  const totalVouchersInCirculation = useMemo(() => {
    const phpTotal = Number(summary.TotalStockAssigned || 0);
    const computedTotal = Number(computedTotalStockAssigned || 0);

    return Math.max(phpTotal, computedTotal);
  }, [summary.TotalStockAssigned, computedTotalStockAssigned]);

  const totalVoucherRedeemed = useMemo(() => {
    const phpTotal = Number(summary.TotalRedeemed || 0);
    const computedTotal = Number(computedTotalRedeemed || 0);

    return Math.max(phpTotal, computedTotal);
  }, [summary.TotalRedeemed, computedTotalRedeemed]);

  const totalVoucherRemaining = useMemo(() => {
    const phpTotal = Number(summary.TotalRemaining || 0);
    const computedTotal = Number(computedTotalRemaining || 0);

    return Math.max(phpTotal, computedTotal);
  }, [summary.TotalRemaining, computedTotalRemaining]);

  const renderAvatar = (creator, size = 58) => {
    const imageUri = normaliseImageUri(creator?.ProfileImage);

    if (imageUri) {
      return (
        <Image
          source={{ uri: imageUri }}
          style={[
            styles.avatarImage,
            {
              width: size,
              height: size,
              borderRadius: size / 2,
            },
          ]}
        />
      );
    }

    return (
      <View
        style={[
          styles.avatarFallback,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
          },
        ]}
      >
        <Text style={styles.avatarFallbackText}>
          {(creator?.Promoter || '?').charAt(0).toUpperCase()}
        </Text>
      </View>
    );
  };

  const openAssignModal = (creator) => {
    addDebug('Creator click received', {
      CollabID: creator?.CollabID,
      Promoter: creator?.Promoter,
    });

    if (!creator) {
      addDebug('Open modal failed: no creator object');
      showVoucherAlert('Debug', 'No creator was passed into the voucher modal.');
      return;
    }

    const collabID = creator?.CollabID ? String(creator.CollabID).trim() : '';

    if (!collabID) {
      addDebug('Open modal failed: creator missing CollabID', creator);
      showVoucherAlert(
        'Debug',
        'This creator has no CollabID, so vouchers cannot be assigned.'
      );
      return;
    }

    setSelectedCreator({
      ...creator,
      CollabID: collabID,
    });
    setWizardStep(1);

    setStock('');
    setOnSpend(
      creator?.OnSpend && Number(creator.OnSpend) > 0
        ? String(Number(creator.OnSpend))
        : ''
    );
    setDiscount(
      creator?.Discount && Number(creator.Discount) > 0
        ? String(Number(creator.Discount))
        : ''
    );

    setSuccessMessage('');
    setModalVisible(true);

    addDebug('Modal opened', {
      selectedCreator: creator?.Promoter,
      CollabID: collabID,
    });
  };

  const closeAssignModal = () => {
    addDebug('Modal close clicked');

    if (submitting) {
      addDebug('Modal close blocked because submitting is true');
      return;
    }

    setModalVisible(false);
    setSelectedCreator(null);
    setWizardStep(1);
    setStock('');
    setOnSpend('');
    setDiscount('');
  };

  const validateOfferStep = () => {
    addDebug('Validating offer step', {
      OnSpend,
      Discount,
    });

    if (!OnSpend || !Discount) {
      addDebug('Offer validation failed: missing fields');
      showVoucherAlert(
        'Missing details',
        'Please enter what customers need to spend and the percentage discount.'
      );
      return false;
    }

    const spendNumber = Number(OnSpend);
    const discountNumber = Number(Discount);

    if (Number.isNaN(spendNumber) || Number.isNaN(discountNumber)) {
      addDebug('Offer validation failed: NaN values', {
        spendNumber,
        discountNumber,
      });
      showVoucherAlert(
        'Invalid details',
        'Customer spend amount and discount must be numbers.'
      );
      return false;
    }

    if (spendNumber <= 0 || discountNumber <= 0) {
      addDebug('Offer validation failed: <= 0');
      showVoucherAlert(
        'Invalid details',
        'Customer spend amount and discount must be greater than 0.'
      );
      return false;
    }

    if (discountNumber > 100) {
      addDebug('Offer validation failed: discount above 100');
      showVoucherAlert('Invalid discount', 'Discount cannot be above 100%.');
      return false;
    }

    addDebug('Offer validation passed');
    return true;
  };

  const validateVoucherStep = () => {
    addDebug('Validating voucher quantity step', {
      Stock,
    });

    if (!Stock) {
      addDebug('Voucher validation failed: missing Stock');
      showVoucherAlert(
        'Missing vouchers',
        'Please enter how many vouchers the creator can share.'
      );
      return false;
    }

    const stockNumber = Number(Stock);

    if (Number.isNaN(stockNumber)) {
      addDebug('Voucher validation failed: Stock NaN');
      showVoucherAlert('Invalid vouchers', 'Voucher quantity must be a number.');
      return false;
    }

    if (stockNumber <= 0) {
      addDebug('Voucher validation failed: Stock <= 0');
      showVoucherAlert('Invalid vouchers', 'Voucher quantity must be greater than 0.');
      return false;
    }

    if (!Number.isInteger(stockNumber)) {
      addDebug('Voucher validation failed: Stock not integer');
      showVoucherAlert('Invalid vouchers', 'Voucher quantity must be a whole number.');
      return false;
    }

    addDebug('Voucher validation passed');
    return true;
  };

  const goNext = () => {
    addDebug('Continue clicked', {
      wizardStep,
      OnSpend,
      Discount,
      Stock,
    });

    if (wizardStep === 1 && !validateOfferStep()) return;
    if (wizardStep === 2 && !validateVoucherStep()) return;

    setWizardStep((step) => {
      const nextStep = Math.min(step + 1, 3);
      addDebug('Wizard moved step', {
        from: step,
        to: nextStep,
      });
      return nextStep;
    });
  };

  const goBackStep = () => {
    addDebug('Back step clicked', {
      wizardStep,
    });

    setWizardStep((step) => Math.max(step - 1, 1));
  };

  const submitVoucherStock = async () => {
    addDebug('Assign vouchers clicked');

    if (submitting) {
      addDebug('Submit blocked: already submitting');
      return;
    }

    if (!selectedCreator) {
      addDebug('Submit stopped: no selectedCreator');
      showVoucherAlert('Missing creator', 'Please select a creator first.');
      return;
    }

    const selectedCollabID = selectedCreator?.CollabID
      ? String(selectedCreator.CollabID).trim()
      : '';

    if (!selectedCollabID) {
      addDebug('Submit stopped: selected creator has no CollabID', selectedCreator);
      showVoucherAlert(
        'Missing CollabID',
        'This creator has no CollabID, so vouchers cannot be assigned.'
      );
      return;
    }

    addDebug('Selected creator on submit', {
      CollabID: selectedCollabID,
      Promoter: selectedCreator.Promoter,
    });

    if (!validateOfferStep()) {
      addDebug('Submit stopped: offer validation failed');
      return;
    }

    if (!validateVoucherStep()) {
      addDebug('Submit stopped: voucher validation failed');
      return;
    }

    try {
      setSubmitting(true);
      setSuccessMessage('');

      const payload = {
        CollabID: selectedCollabID,
        Stock: Number(Stock),
        OnSpend: Number(OnSpend),
        Discount: Number(Discount),
      };

      addDebug('Sending insert payload', payload);

      const response = await fetch(INSERT_VOUCHER_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const rawText = await response.text();

      addDebug('Insert HTTP status', response.status);
      addDebug('Insert raw PHP response', rawText.slice(0, 1000));

      let data;

      try {
        data = JSON.parse(rawText);
      } catch (jsonError) {
        addDebug('Insert JSON parse failed', rawText.slice(0, 1000));
        throw new Error(`Insert PHP returned invalid JSON: ${rawText.slice(0, 220)}`);
      }

      addDebug('Insert parsed response', data);

      if (!response.ok || !data.success) {
        const debugText =
          typeof data.debug === 'object'
            ? JSON.stringify(data.debug)
            : data.debug || '';

        throw new Error(
          `${data.message || `Insert HTTP ${response.status}`}${debugText ? ` | ${debugText}` : ''}`
        );
      }

      if (data.InsertVerified !== true) {
        throw new Error(
          `PHP returned success, but InsertVerified was not true. Debug: ${JSON.stringify(data)}`
        );
      }

      setSuccessMessage('Creator vouchers assigned successfully.');

      addDebug('Insert verified successfully', {
        CollabID: data.CollabID,
        Promoter: data.Promoter,
        BrandName: data.BrandName,
        Stock: data.Stock,
        OnSpend: data.OnSpend,
        Discount: data.Discount,
        VerifiedRow: data.VerifiedRow,
      });

      await fetchAll();

      showVoucherAlert(
        'Voucher insert success',
        `Inserted and verified.\n\nCollabID: ${data.CollabID}\nCreator: ${data.Promoter}\nStock: ${data.Stock}\nSpend: £${data.OnSpend}\nDiscount: ${data.Discount}%`
      );

      setModalVisible(false);
      setSelectedCreator(null);
      setWizardStep(1);
      setStock('');
      setOnSpend('');
      setDiscount('');
    } catch (error) {
      console.error('Insert voucher stock error:', error);
      addDebug('Insert failed', error.message);

      showVoucherAlert(
        'Voucher insert failed',
        error.message || 'Something went wrong assigning creator vouchers.'
      );
    } finally {
      setSubmitting(false);
      addDebug('Submit flow finished');
    }
  };

  const toggleVoucherExpanded = (creator) => {
    const key = getCreatorKey(creator);

    setExpandedVoucherKey((current) => {
      if (current === key) {
        return null;
      }

      return key;
    });
  };

  const renderDebugBox = () => {
    if (!DEBUG_VOUCHERS) return null;

    return (
      <View style={styles.debugBox}>
        <View style={styles.debugHeaderRow}>
          <Text style={styles.debugTitle}>Voucher debug</Text>

          <TouchableOpacity
            onPress={() => setDebugMessages([])}
            activeOpacity={0.8}
          >
            <Text style={styles.debugClear}>Clear</Text>
          </TouchableOpacity>
        </View>

        {debugMessages.length === 0 ? (
          <Text style={styles.debugLine}>No voucher actions logged yet.</Text>
        ) : (
          debugMessages.map((message, index) => (
            <Text key={`${message}-${index}`} style={styles.debugLine}>
              {message}
            </Text>
          ))
        )}
      </View>
    );
  };

  const renderSideNav = () => {
    return (
      <View style={styles.sideNav}>
        <LinearGradient
          colors={['#050509', '#050505', '#000000']}
          style={styles.sideNavInner}
        >
          <LinearGradient
            colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
            style={styles.sideNavGlow}
            start={{ x: 0.1, y: 0 }}
            end={{ x: 1, y: 1 }}
          />

          <View style={styles.sideNavHeader}>
            <Image
              source={{
                uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
              }}
              style={styles.sideNavLogoImage}
              resizeMode="contain"
            />
            <View>
              <Text style={styles.logoWordmark}>
                <Text style={{ fontWeight: '300' }}>collab</Text>
                bay
                <Text style={styles.logoDot}>.</Text>
              </Text>
              <Text style={styles.logoDesktopTag}>DESKTOP</Text>
            </View>
          </View>

          <View style={styles.sideNavSection}>
            <LinearGradient
              colors={['#111111', '#2b2b2b']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.usernamePill}
            >
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={styles.usernamePillText}
              >
                {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
              </Text>
            </LinearGradient>

            <TouchableOpacity
              style={styles.navItem}
              onPress={() => navigation.navigate('MyPanel')}
            >
              <View style={styles.navItemRow}>
                <View style={styles.navItemMainLabel}>
                  <Ionicons
                    name="home-outline"
                    size={20}
                    color="#f5f5f5"
                    style={styles.navIcon}
                  />
                  <Text style={styles.navText}>Home</Text>
                </View>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.navItem}
              onPress={() => navigation.navigate('MyOffers')}
            >
              <View style={styles.navItemRow}>
                <View style={styles.navItemMainLabel}>
                  <Ionicons
                    name="pricetag-outline"
                    size={20}
                    color="#f5f5f5"
                    style={styles.navIcon}
                  />
                  <Text style={styles.navText}>My Offers</Text>
                </View>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.navItem}
              onPress={() => {
                if (authChecked && !isAuthed) {
                  openInSameTab('https://collabbayadmin.com/subscribe');
                } else {
                  navigation.navigate('MyCalendar');
                }
              }}
            >
              <View style={styles.navItemRow}>
                <View style={styles.navItemMainLabel}>
                  <Ionicons
                    name="calendar-outline"
                    size={20}
                    color="#f5f5f5"
                    style={styles.navIcon}
                  />
                  <Text style={styles.navText}>My Calendar</Text>
                </View>
                {authChecked && !isAuthed && (
                  <Ionicons
                    name="lock-closed-outline"
                    size={16}
                    color="#FDF05D"
                    style={styles.lockIcon}
                  />
                )}
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.navItem}
              onPress={() => navigation.navigate('BrowseCreators')}
            >
              <View style={styles.navItemRow}>
                <View style={styles.navItemMainLabel}>
                  <Ionicons
                    name="people-outline"
                    size={20}
                    color="#f5f5f5"
                    style={styles.navIcon}
                  />
                  <Text style={styles.navText}>Browse Creators</Text>
                </View>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.navItem}
              onPress={() => {
                if (authChecked && !isAuthed) {
                  openInSameTab('https://collabbayadmin.com/subscribe');
                } else {
                  navigation.navigate('MyActivityFeed');
                }
              }}
            >
              <View style={styles.navItemRow}>
                <View style={styles.navItemMainLabel}>
                  <Ionicons
                    name="chatbubbles-outline"
                    size={20}
                    color="#f5f5f5"
                    style={styles.navIcon}
                  />
                  <Text style={styles.navText}>Activity</Text>
                </View>
                {authChecked && !isAuthed && (
                  <Ionicons
                    name="lock-closed-outline"
                    size={16}
                    color="#FDF05D"
                    style={styles.lockIcon}
                  />
                )}
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.navItem}
              onPress={() => navigation.navigate('BusinessAccountPage')}
            >
              <View style={styles.navItemRow}>
                <View style={styles.navItemMainLabel}>
                  <Ionicons
                    name="person-circle-outline"
                    size={20}
                    color="#f5f5f5"
                    style={styles.navIcon}
                  />
                  <Text style={styles.navText}>Account</Text>
                </View>
              </View>
            </TouchableOpacity>
          </View>

          <View style={styles.sideNavSection}>
            <TouchableOpacity
              style={styles.peerMapCard}
              activeOpacity={0.9}
              onPress={() => openInSameTab('/venues')}
            >
              <LinearGradient
                colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                style={styles.peerMapInner}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              >
                <View style={styles.peerMapContent}>
                  <Ionicons
                    name="map-outline"
                    size={18}
                    color="#FDF05D"
                    style={{ marginRight: 8 }}
                  />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.peerMapTitle}>Peer Map</Text>
                    <Text style={styles.peerMapSubtitle}>
                      View 100+ venues on Collabbay
                    </Text>
                  </View>
                </View>
              </LinearGradient>
            </TouchableOpacity>

            {!isSubscriber && (
              <TouchableOpacity
                style={styles.subscribeCard}
                activeOpacity={0.9}
                onPress={() =>
                  openInSameTab('https://collabbayadmin.com/subscribe')
                }
              >
                <LinearGradient
                  colors={['#FDF05D', '#fbbf24']}
                  style={styles.subscribeGradient}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                >
                  <View style={styles.subscribeContent}>
                    <Ionicons
                      name="rocket-outline"
                      size={20}
                      color="#000"
                      style={{ marginRight: 8 }}
                    />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.subscribeTitle}>
                        Subscribe & unlock
                      </Text>
                      <Text style={styles.subscribeSubtitle}>
                        Unlock Activity & Calendar, plus more.
                      </Text>
                    </View>
                  </View>
                </LinearGradient>
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.sideNavBottom}>
            <TouchableOpacity
              style={styles.bottomLink}
              onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}
            >
              <Ionicons
                name="help-circle-outline"
                size={18}
                color="#f5f5f5"
                style={{ marginRight: 8 }}
              />
              <View>
                <Text style={styles.bottomLinkText}>FAQs</Text>
                <Text style={styles.bottomLinkCaption}>
                  Common questions answered
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.bottomLink}
              onPress={() => Linking.openURL('mailto:info@collab-bay.com')}
            >
              <Ionicons
                name="headset-outline"
                size={18}
                color="#f5f5f5"
                style={{ marginRight: 8 }}
              />
              <View>
                <Text style={styles.bottomLinkText}>Help</Text>
                <Text style={styles.bottomLinkCaption}>
                  We're here 24/7
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        </LinearGradient>
      </View>
    );
  };

  const renderModalStep = () => {
    if (!selectedCreator) return null;

    if (wizardStep === 1) {
      return (
        <View>
          <Text style={styles.modalStepLabel}>Step 1 of 3</Text>
          <Text style={styles.modalTitle}>Set the creator voucher</Text>
          <Text style={styles.modalSubtitle}>
            Choose what customers need to spend, then enter the percentage discount
            they get through this creator.
          </Text>

          <View style={styles.modalInputGroup}>
            <Text style={styles.modalInputLabel}>When customers spend</Text>
            <Text style={styles.modalHelpText}>
              Example: when customers spend £50, they unlock the creator voucher.
            </Text>

            <View style={styles.inputWithPrefix}>
              <Text style={styles.inputPrefix}>£</Text>
              <TextInput
                style={styles.modalInput}
                value={OnSpend}
                onChangeText={(value) => {
                  setOnSpend(value);
                  addDebug('OnSpend changed', value);
                }}
                placeholder="50"
                keyboardType="numeric"
                placeholderTextColor="#aaa"
              />
            </View>
          </View>

          <View style={styles.modalInputGroup}>
            <Text style={styles.modalInputLabel}>Give them</Text>
            <Text style={styles.modalHelpText}>
              Enter the percentage off. Example: 20 means customers get 20% off.
            </Text>

            <View style={styles.inputWithPrefix}>
              <TextInput
                style={styles.modalInput}
                value={Discount}
                onChangeText={(value) => {
                  setDiscount(value);
                  addDebug('Discount changed', value);
                }}
                placeholder="20"
                keyboardType="numeric"
                placeholderTextColor="#aaa"
              />
              <Text style={styles.inputSuffix}>%</Text>
            </View>
          </View>
        </View>
      );
    }

    if (wizardStep === 2) {
      return (
        <View>
          <Text style={styles.modalStepLabel}>Step 2 of 3</Text>
          <Text style={styles.modalTitle}>Choose how many to share</Text>
          <Text style={styles.modalSubtitle}>
            This is the number of vouchers this creator can share with their audience
            to help drive more footfall.
          </Text>

          <View style={styles.modalInputGroup}>
            <Text style={styles.modalInputLabel}>Creator vouchers</Text>
            <Text style={styles.modalHelpText}>
              Example: give this creator 100 vouchers to share.
            </Text>

            <TextInput
              style={styles.bigStockInput}
              value={Stock}
              onChangeText={(value) => {
                setStock(value);
                addDebug('Stock changed', value);
              }}
              placeholder="100"
              keyboardType="numeric"
              placeholderTextColor="#aaa"
            />
          </View>

          <View style={styles.stockSuggestionRow}>
            {[25, 50, 100, 250].map((amount) => (
              <TouchableOpacity
                key={amount}
                style={[
                  styles.stockSuggestion,
                  Number(Stock) === amount && styles.stockSuggestionActive,
                ]}
                onPress={() => {
                  setStock(String(amount));
                  addDebug('Stock suggestion clicked', amount);
                }}
              >
                <Text
                  style={[
                    styles.stockSuggestionText,
                    Number(Stock) === amount && styles.stockSuggestionTextActive,
                  ]}
                >
                  {amount}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      );
    }

    return (
      <View>
        <Text style={styles.modalStepLabel}>Step 3 of 3</Text>
        <Text style={styles.modalTitle}>Review and assign</Text>
        <Text style={styles.modalSubtitle}>
          Confirm the creator voucher before it goes live.
        </Text>

        <View style={styles.reviewCreatorCard}>
          {renderAvatar(selectedCreator, 64)}

          <View style={{ flex: 1 }}>
            <Text style={styles.reviewCreatorName}>
              {selectedCreator.Promoter}
            </Text>
            <Text style={styles.reviewCreatorMeta}>
              {formatDaysAgo(selectedCreator.DaysAgo)}
            </Text>
            <Text style={styles.reviewCreatorMetaStrong}>
              {formatFollowersLabel(selectedCreator.PlatformFollowers)}
            </Text>
          </View>
        </View>

        <View style={styles.voucherPreviewLarge}>
          <View style={styles.previewIconCircle}>
            <Ionicons name="ticket-outline" size={24} color="#111" />
          </View>

          <Text style={styles.voucherPreviewTitle}>
            When customers spend £{OnSpend || 'Y'}, give them {Discount || 'X'}% off
          </Text>

          <Text style={styles.voucherPreviewText}>
            {selectedCreator.Promoter} will receive {Stock || '0'} vouchers to share
            with their audience and help boost footfall.
          </Text>
        </View>

        <View style={styles.reviewGrid}>
          <View style={styles.reviewBox}>
            <Text style={styles.reviewBoxLabel}>Customers spend</Text>
            <Text style={styles.reviewBoxValue}>£{OnSpend || '0'}</Text>
          </View>

          <View style={styles.reviewBox}>
            <Text style={styles.reviewBoxLabel}>They get</Text>
            <Text style={styles.reviewBoxValue}>{Discount || '0'}%</Text>
          </View>

          <View style={styles.reviewBox}>
            <Text style={styles.reviewBoxLabel}>Vouchers</Text>
            <Text style={styles.reviewBoxValue}>{Stock || '0'}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderVoucherStockRow = (creator) => {
    const creatorKey = getCreatorKey(creator);
    const isExpanded = expandedVoucherKey === creatorKey;

    const stockAssigned = Number(creator.StockAssigned || 0);
    const redeemed = Number(creator.Redeemed || 0);
    const remaining = Number(creator.Remaining || 0);
    const onSpend = Number(creator.OnSpend || 0);
    const discount = Number(creator.Discount || 0);

    return (
      <View key={`stock-${creatorKey}`} style={styles.stockAccordionWrapper}>
        <TouchableOpacity
          style={styles.stockTableRow}
          activeOpacity={0.9}
          onPress={() => toggleVoucherExpanded(creator)}
        >
          <View style={styles.stockTableCreator}>
            {renderAvatar(creator, 40)}

            <View style={{ flex: 1 }}>
              <Text style={styles.stockTableName} numberOfLines={1}>
                {creator.Promoter}
              </Text>
              <Text style={styles.stockTableMeta} numberOfLines={1}>
                {formatNumber(stockAssigned)} vouchers assigned
              </Text>
            </View>
          </View>

          <View style={styles.stockTableStats}>
            <Text style={styles.stockTableStatText}>
              {formatNumber(stockAssigned)} in circulation
            </Text>
            <View style={styles.stockChevronRow}>
              <Text style={styles.stockTableStatMuted}>
                {isExpanded ? 'Hide details' : 'View details'}
              </Text>
              <Ionicons
                name={isExpanded ? 'chevron-up' : 'chevron-down'}
                size={15}
                color="#777"
                style={{ marginLeft: 4 }}
              />
            </View>
          </View>
        </TouchableOpacity>

        {isExpanded ? (
          <View style={styles.stockExpandedBox}>
            <View style={styles.stockExpandedGrid}>
              <View style={styles.stockExpandedMetric}>
                <Text style={styles.stockExpandedLabel}>Creator</Text>
                <Text style={styles.stockExpandedValue} numberOfLines={1}>
                  {creator.Promoter}
                </Text>
              </View>

              <View style={styles.stockExpandedMetric}>
                <Text style={styles.stockExpandedLabel}>CollabID</Text>
                <Text style={styles.stockExpandedValue} numberOfLines={1}>
                  {String(creator.CollabID || '')}
                </Text>
              </View>

              <View style={styles.stockExpandedMetric}>
                <Text style={styles.stockExpandedLabel}>Stock</Text>
                <Text style={styles.stockExpandedValue}>
                  {formatNumber(stockAssigned)}
                </Text>
              </View>

              <View style={styles.stockExpandedMetric}>
                <Text style={styles.stockExpandedLabel}>Redeemed</Text>
                <Text style={styles.stockExpandedValue}>
                  {formatNumber(redeemed)}
                </Text>
              </View>

              <View style={styles.stockExpandedMetric}>
                <Text style={styles.stockExpandedLabel}>Remaining</Text>
                <Text style={styles.stockExpandedValue}>
                  {formatNumber(remaining)}
                </Text>
              </View>

              <View style={styles.stockExpandedMetric}>
                <Text style={styles.stockExpandedLabel}>Voucher offer</Text>
                <Text style={styles.stockExpandedValue}>
                  £{formatMoney(onSpend)} spend / {formatNumber(discount)}% off
                </Text>
              </View>
            </View>

     <View style={styles.stockExpandedFooter}>
  <Text style={styles.stockExpandedFooterText}>
    These are vouchers assigned to this creator from voucherstock.
  </Text>
</View>
          </View>
        ) : null}
      </View>
    );
  };

  const renderMainContent = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="black" />
          <Text style={styles.loadingText}>Loading voucher dashboard…</Text>
        </View>
      );
    }

    return (
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.contentWrapper}>
          <View style={styles.pageHeaderRow}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={styles.backButton}
            >
              <Ionicons name="arrow-back" size={20} color="black" />
              <Text style={styles.backButtonText}>Back</Text>
            </TouchableOpacity>

            <View style={styles.headerRight}>
              <View style={styles.brandPill}>
                <Ionicons name="business-outline" size={15} color="#111" />
                <Text style={styles.brandPillText}>
                  {brandName || 'Brand'}
                </Text>
              </View>

              <TouchableOpacity
                onPress={() => Linking.openURL('https://collabbayadmin.com')}
                style={styles.closeButton}
              >
                <Ionicons name="close" size={22} color="#000" />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.topBadgeRow}>
            <TouchableOpacity
              style={styles.voucherBadge}
              activeOpacity={0.85}
              onPress={() => {
                addDebug('Info badge clicked');
                setInfoModalVisible(true);
              }}
            >
              <Ionicons name="ticket-outline" size={14} color="#111" />
              <Text style={styles.voucherBadgeText}>
                VOUCHERS — BY COLLABBAY™
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.infoPill}
              activeOpacity={0.85}
              onPress={() => {
                addDebug('What is this clicked');
                setInfoModalVisible(true);
              }}
            >
              <Ionicons name="information-circle-outline" size={16} color="#111" />
              <Text style={styles.infoPillText}>WHAT IS THIS?</Text>
            </TouchableOpacity>
          </View>

          {renderDebugBox()}

          <View
            style={[
              styles.heroCard,
              !isWideHero && styles.heroCardStacked,
            ]}
          >
            <View style={styles.heroTextBlock}>
              <Text style={styles.eyebrow}>Designed to boost footfall</Text>
              <Text style={styles.title}>Creator vouchers</Text>
              <Text style={styles.subtitle}>
                Turn creator content into real customer visits. Give creators
                limited voucher batches to share with their audience, increase
                venue footfall, and track exactly what gets redeemed.
              </Text>
            </View>

            <View
              style={[
                styles.heroRightCluster,
                !isWideHero && styles.heroRightClusterStacked,
              ]}
            >
              <View style={styles.heroFloatingCard}>
                <Ionicons name="ticket-outline" size={24} color="#111" />
                <Text style={styles.heroFloatingTitle}>
                  {formatNumber(totalVouchersInCirculation)}
                </Text>
                <Text style={styles.heroFloatingText}>
                  vouchers in circulation
                </Text>
                <Text style={styles.heroFloatingSubText}>
                  {formatNumber(creatorsWithVouchers.length)} creators currently live
                </Text>
              </View>

              <View style={styles.heroGifCard}>
                <Image
                  source={{ uri: GIF_URL }}
                  style={styles.heroGifImage}
                  resizeMode="contain"
                />
              </View>
            </View>
          </View>

          <View style={styles.summaryGrid}>
            <View style={styles.summaryCard}>
              <View style={styles.summaryIcon}>
                <Ionicons name="people-outline" size={18} color="#111" />
              </View>
              <Text style={styles.summaryValue}>
                {formatNumber(summary.EligibleCreators)}
              </Text>
              <Text style={styles.summaryLabel}>Eligible creators</Text>
            </View>

            <View style={styles.summaryCard}>
              <View style={styles.summaryIcon}>
                <Ionicons name="ticket-outline" size={18} color="#111" />
              </View>
              <Text style={styles.summaryValue}>
                {formatNumber(totalVouchersInCirculation)}
              </Text>
              <Text style={styles.summaryLabel}>Vouchers in circulation</Text>
            </View>

            <View style={styles.summaryCard}>
              <View style={styles.summaryIcon}>
                <Ionicons name="checkmark-circle-outline" size={18} color="#111" />
              </View>
              <Text style={styles.summaryValue}>
                {formatNumber(totalVoucherRedeemed)}
              </Text>
              <Text style={styles.summaryLabel}>Redeemed</Text>
            </View>

            <View style={styles.summaryCard}>
              <View style={styles.summaryIcon}>
                <Ionicons name="trending-up-outline" size={18} color="#111" />
              </View>
              <Text style={styles.summaryValue}>
                {formatNumber(creatorsWithVouchers.length || summary.LiveCreators)}
              </Text>
              <Text style={styles.summaryLabel}>Creators live</Text>
            </View>
          </View>

          {successMessage ? (
            <View style={styles.successBox}>
              <Ionicons name="checkmark-circle" size={22} color="#0a7d28" />
              <Text style={styles.successText}>{successMessage}</Text>
            </View>
          ) : null}

          <View style={styles.sectionHeaderRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.sectionTitle}>
                Select a creator to boost footfall
              </Text>
              <Text style={styles.sectionSubtext}>
                These are creators who have already delivered content. Give them
                vouchers to share with their audience and turn that exposure into
                measurable visits.
              </Text>
            </View>

            <TouchableOpacity style={styles.refreshButton} onPress={onRefresh}>
              <Ionicons name="refresh-outline" size={17} color="#111" />
              <Text style={styles.refreshButtonText}>Refresh</Text>
            </TouchableOpacity>
          </View>

          {creators.length === 0 ? (
            <View style={styles.emptyBox}>
              <Ionicons name="alert-circle-outline" size={28} color="#777" />
              <Text style={styles.emptyTitle}>No eligible creators found</Text>
              <Text style={styles.emptyText}>
                {err || 'Once a creator has delivered content, they should appear here.'}
              </Text>
            </View>
          ) : (
            <View style={styles.creatorGrid}>
              {creators.map((creator) => {
                const vouchersShared = Number(creator.StockAssigned || 0);
                const redeemed = Number(creator.Redeemed || 0);
                const inCirculation = Number(creator.Remaining || 0);

                return (
                  <TouchableOpacity
                    key={`${creator.CollabID}-${creator.Promoter}`}
                    style={[
                      styles.creatorCard,
                      { width: isDesktop ? '48.8%' : '100%' },
                    ]}
                    activeOpacity={0.9}
                    onPress={() => openAssignModal(creator)}
                  >
                    <View style={styles.creatorTopRow}>
                      {renderAvatar(creator, 62)}

                      <View style={styles.creatorMainInfo}>
                        <Text style={styles.creatorName} numberOfLines={1}>
                          {creator.Promoter}
                        </Text>

                        <Text style={styles.creatorMeta} numberOfLines={1}>
                          {formatDaysAgo(creator.DaysAgo)}
                        </Text>
                      </View>

                      <View style={styles.cardArrow}>
                        <Ionicons name="chevron-forward" size={18} color="#111" />
                      </View>
                    </View>

                    <View style={styles.followerHighlightRow}>
                      <View style={styles.followerHighlight}>
                        <Ionicons
                          name="people-outline"
                          size={16}
                          color="#111"
                          style={{ marginRight: 8 }}
                        />
                        <Text style={styles.followerHighlightText}>
                          {formatFollowersLabel(creator.PlatformFollowers)}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.creatorDivider} />

                    <View style={styles.creatorStatsRow}>
                      <View style={styles.creatorStat}>
                        <Text style={styles.creatorStatValue}>
                          {formatNumber(vouchersShared)}
                        </Text>
                        <Text style={styles.creatorStatLabel}>Assigned</Text>
                      </View>

                      <View style={styles.creatorStat}>
                        <Text style={styles.creatorStatValue}>
                          {formatNumber(redeemed)}
                        </Text>
                        <Text style={styles.creatorStatLabel}>Redeemed</Text>
                      </View>

                      <View style={styles.creatorStat}>
                        <Text style={styles.creatorStatValue}>
                          {formatNumber(inCirculation)}
                        </Text>
                        <Text style={styles.creatorStatLabel}>Remaining</Text>
                      </View>
                    </View>

                    {vouchersShared > 0 ? (
                      <View style={styles.creatorVoucherLivePill}>
                        <Ionicons
                          name="ticket-outline"
                          size={14}
                          color="#111"
                          style={{ marginRight: 6 }}
                        />
                        <Text style={styles.creatorVoucherLiveText}>
                          {formatNumber(vouchersShared)} vouchers in circulation
                        </Text>
                      </View>
                    ) : null}

                    <View style={styles.creatorBottomRow}>
                      <Text style={styles.creatorBrandText} numberOfLines={1}>
                        Tap to give this creator vouchers and drive more visits
                      </Text>

                      <TouchableOpacity
                        style={styles.assignMiniButton}
                        activeOpacity={0.85}
                        onPress={() => openAssignModal(creator)}
                      >
                        <Text style={styles.assignMiniButtonText}>
                          {vouchersShared > 0 ? 'Add vouchers' : 'Open'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          <View style={styles.currentStockSection}>
            <View style={styles.currentStockHeaderRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.currentStockTitle}>
                  Your vouchers in circulation
                </Text>
                <Text style={styles.currentStockSubtitle}>
                  {formatNumber(totalVouchersInCirculation)} vouchers are currently assigned
                  across {formatNumber(creatorsWithVouchers.length)} creator
                  {creatorsWithVouchers.length === 1 ? '' : 's'}. Tap a creator to expand.
                </Text>
              </View>

              <View style={styles.currentStockTotalPill}>
                <Text style={styles.currentStockTotalNumber}>
                  {formatNumber(totalVouchersInCirculation)}
                </Text>
                <Text style={styles.currentStockTotalLabel}>total</Text>
              </View>
            </View>

            {creatorsWithVouchers.length === 0 ? (
              <View style={styles.noStockBox}>
                <Ionicons name="ticket-outline" size={24} color="#777" />
                <Text style={styles.noStockTitle}>No vouchers in circulation yet</Text>
                <Text style={styles.noStockText}>
                  Once you assign vouchers to a creator, they will appear here.
                </Text>
              </View>
            ) : (
              <View style={styles.stockTable}>
                {creatorsWithVouchers.map((creator) => renderVoucherStockRow(creator))}
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.appShell}>
        <View style={styles.shellRow}>
          {renderSideNav()}
          <View style={styles.mainArea}>{renderMainContent()}</View>
        </View>
      </View>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={closeAssignModal}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalShell}>
            <ScrollView
              contentContainerStyle={styles.modalScrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.modalCard}>
                <View style={styles.modalTopRow}>
                  <View style={styles.modalCreatorIntro}>
                    {selectedCreator ? renderAvatar(selectedCreator, 48) : null}

                    <View style={{ flex: 1 }}>
                      <Text style={styles.modalCreatorName} numberOfLines={1}>
                        {selectedCreator?.Promoter || 'Creator'}
                      </Text>
                      <Text style={styles.modalCreatorMeta}>
                        Set up creator vouchers
                      </Text>
                    </View>
                  </View>

                  <TouchableOpacity
                    style={styles.modalCloseButton}
                    onPress={closeAssignModal}
                  >
                    <Ionicons name="close" size={21} color="#111" />
                  </TouchableOpacity>
                </View>

                <View style={styles.progressRow}>
                  {[1, 2, 3].map((step) => (
                    <View
                      key={step}
                      style={[
                        styles.progressDot,
                        wizardStep >= step && styles.progressDotActive,
                      ]}
                    />
                  ))}
                </View>

                {renderModalStep()}

                <View style={styles.modalButtonRow}>
                  {wizardStep > 1 ? (
                    <TouchableOpacity
                      style={styles.modalSecondaryButton}
                      onPress={goBackStep}
                      disabled={submitting}
                    >
                      <Text style={styles.modalSecondaryButtonText}>Back</Text>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      style={styles.modalSecondaryButton}
                      onPress={closeAssignModal}
                      disabled={submitting}
                    >
                      <Text style={styles.modalSecondaryButtonText}>Cancel</Text>
                    </TouchableOpacity>
                  )}

                  {wizardStep < 3 ? (
                    <TouchableOpacity
                      style={styles.modalPrimaryButton}
                      onPress={goNext}
                    >
                      <Text style={styles.modalPrimaryButtonText}>Continue</Text>
                      <Ionicons name="arrow-forward" size={17} color="#111" />
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      style={[
                        styles.modalPrimaryButton,
                        submitting && styles.modalPrimaryButtonDisabled,
                      ]}
                      onPress={submitVoucherStock}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <ActivityIndicator color="#111" />
                      ) : (
                        <>
                          <Text style={styles.modalPrimaryButtonText}>
                            Assign vouchers
                          </Text>
                          <Ionicons name="checkmark" size={18} color="#111" />
                        </>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        visible={infoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setInfoModalVisible(false)}
      >
        <View style={styles.infoBackdrop}>
          <View style={styles.infoCard}>
            <View style={styles.infoTopRow}>
              <View style={styles.infoIconCircle}>
                <Ionicons name="ticket-outline" size={24} color="#111" />
              </View>

              <TouchableOpacity
                style={styles.infoCloseButton}
                onPress={() => setInfoModalVisible(false)}
              >
                <Ionicons name="close" size={20} color="#111" />
              </TouchableOpacity>
            </View>

            <Text style={styles.infoBadgeText}>VOUCHERS — BY COLLABBAY™</Text>
            <Text style={styles.infoTitle}>What is this?</Text>

            <Text style={styles.infoBody}>
              This tool lets you give creators a limited batch of trackable vouchers
              after they have delivered content for your venue. They can share the
              voucher with their audience to help boost footfall.
            </Text>

            <View style={styles.infoPoint}>
              <Text style={styles.infoPointNumber}>1</Text>
              <Text style={styles.infoPointText}>
                Set the offer clearly: when customers spend a certain amount, give them
                a percentage off.
              </Text>
            </View>

            <View style={styles.infoPoint}>
              <Text style={styles.infoPointNumber}>2</Text>
              <Text style={styles.infoPointText}>
                Choose how many vouchers the creator can share with their audience.
              </Text>
            </View>

            <View style={styles.infoPoint}>
              <Text style={styles.infoPointNumber}>3</Text>
              <Text style={styles.infoPointText}>
                Collabbay tracks vouchers shared, vouchers redeemed, and what is still
                actively in circulation.
              </Text>
            </View>

            <TouchableOpacity
              style={styles.infoPrimaryButton}
              onPress={() => setInfoModalVisible(false)}
            >
              <Text style={styles.infoPrimaryButtonText}>Got it</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  appShell: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  mainArea: {
    flex: 1,
    backgroundColor: '#f7f5f1',
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    paddingBottom: 70,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 1120,
    alignSelf: 'center',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#f7f5f1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    fontSize: 13,
    color: '#6b7280',
  },

  debugBox: {
    backgroundColor: '#111',
    borderRadius: 18,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  debugHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  debugTitle: {
    color: '#FDF05D',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  debugClear: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '800',
  },
  debugLine: {
    color: '#e8e8e8',
    fontSize: 11,
    lineHeight: 16,
    marginTop: 3,
  },

  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#eee',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },
  closeButton: {
    backgroundColor: '#fff',
    padding: 6,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
    marginLeft: 8,
  },
  brandPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  brandPillText: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '800',
    color: '#111',
  },

  topBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    flexWrap: 'wrap',
    gap: 10,
  },
  voucherBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 13,
    borderWidth: 1,
    borderColor: '#eee061',
  },
  voucherBadgeText: {
    marginLeft: 7,
    fontSize: 11,
    color: '#111',
    fontWeight: '900',
    letterSpacing: 1.1,
  },
  infoPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 13,
    borderWidth: 1,
    borderColor: '#eee7dc',
  },
  infoPillText: {
    marginLeft: 6,
    fontSize: 11,
    color: '#111',
    fontWeight: '900',
    letterSpacing: 1,
  },

  heroCard: {
    backgroundColor: '#ffffff',
    borderRadius: 32,
    padding: 24,
    marginBottom: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 3,
  },
  heroCardStacked: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  heroTextBlock: {
    flex: 1,
    paddingRight: 18,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: '900',
    color: '#85724b',
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 8,
  },
  title: {
    fontSize: 32,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.8,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
    lineHeight: 22,
    maxWidth: 680,
  },
  heroRightCluster: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 12,
    marginLeft: 10,
  },
  heroRightClusterStacked: {
    marginTop: 18,
    marginLeft: 0,
    width: '100%',
    flexWrap: 'wrap',
  },
  heroFloatingCard: {
    width: 174,
    minHeight: 176,
    borderRadius: 26,
    backgroundColor: '#f7f5f1',
    padding: 18,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    borderWidth: 1,
    borderColor: '#eee7dc',
  },
  heroFloatingTitle: {
    marginTop: 12,
    fontSize: 28,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.6,
  },
  heroFloatingText: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '800',
    color: '#555',
    lineHeight: 17,
  },
  heroFloatingSubText: {
    marginTop: 10,
    fontSize: 12,
    fontWeight: '800',
    color: '#8a7a58',
  },
  heroGifCard: {
    width: 174,
    minHeight: 176,
    borderRadius: 26,
    backgroundColor: '#111',
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  heroGifImage: {
    width: '100%',
    height: 150,
    borderRadius: 18,
  },

  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  summaryCard: {
    flex: 1,
    minWidth: 180,
    backgroundColor: '#ffffff',
    borderRadius: 24,
    padding: 18,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
  },
  summaryIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#f4efe6',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  summaryValue: {
    fontSize: 26,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.4,
  },
  summaryLabel: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '700',
    color: '#777',
  },

  successBox: {
    backgroundColor: '#eaf8ee',
    borderWidth: 1,
    borderColor: '#bfe7ca',
    borderRadius: 18,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  successText: {
    marginLeft: 8,
    color: '#0a7d28',
    fontWeight: '800',
    fontSize: 14,
  },

  sectionHeaderRow: {
    marginTop: 6,
    marginBottom: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.4,
  },
  sectionSubtext: {
    marginTop: 4,
    fontSize: 14,
    color: '#777',
    lineHeight: 20,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  refreshButtonText: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '800',
    color: '#111',
  },

  creatorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  creatorCard: {
    backgroundColor: '#ffffff',
    borderRadius: 28,
    padding: 16,
    shadowColor: '#000',
    shadowOpacity: 0.055,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 7 },
    elevation: 2,
    borderWidth: 1,
    borderColor: '#f0ece5',
  },
  creatorTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarImage: {
    backgroundColor: '#eee',
  },
  avatarFallback: {
    backgroundColor: '#f1eadf',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    color: '#111',
    fontWeight: '900',
    fontSize: 18,
  },
  creatorMainInfo: {
    flex: 1,
    marginLeft: 12,
  },
  creatorName: {
    fontSize: 17,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.2,
  },
  creatorMeta: {
    marginTop: 3,
    fontSize: 12,
    color: '#777',
    fontWeight: '600',
  },
  cardArrow: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#f7f5f1',
    alignItems: 'center',
    justifyContent: 'center',
  },

  followerHighlightRow: {
    marginTop: 14,
  },
  followerHighlight: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  followerHighlightText: {
    color: '#111',
    fontSize: 13,
    fontWeight: '900',
  },

  creatorDivider: {
    height: 1,
    backgroundColor: '#f1eee8',
    marginVertical: 14,
  },
  creatorStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  creatorStat: {
    flex: 1,
    backgroundColor: '#faf8f4',
    borderRadius: 18,
    paddingVertical: 12,
    paddingHorizontal: 10,
  },
  creatorStatValue: {
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
  },
  creatorStatLabel: {
    marginTop: 2,
    fontSize: 11,
    fontWeight: '700',
    color: '#777',
  },
  creatorVoucherLivePill: {
    marginTop: 12,
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  creatorVoucherLiveText: {
    color: '#111',
    fontSize: 12,
    fontWeight: '900',
  },
  creatorBottomRow: {
    marginTop: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  creatorBrandText: {
    flex: 1,
    color: '#777',
    fontSize: 12,
    fontWeight: '700',
    marginRight: 10,
  },
  assignMiniButton: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  assignMiniButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '900',
  },

  emptyBox: {
    backgroundColor: '#ffffff',
    borderRadius: 28,
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
  },
  emptyTitle: {
    marginTop: 10,
    fontSize: 17,
    fontWeight: '900',
    color: '#111',
  },
  emptyText: {
    marginTop: 4,
    color: '#777',
    fontSize: 13,
    textAlign: 'center',
  },

  currentStockSection: {
    marginTop: 24,
    backgroundColor: '#ffffff',
    borderRadius: 30,
    padding: 20,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
  },
  currentStockHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
    marginBottom: 14,
  },
  currentStockTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#111',
  },
  currentStockSubtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#777',
    lineHeight: 19,
  },
  currentStockTotalPill: {
    backgroundColor: '#111',
    borderRadius: 18,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
    minWidth: 88,
  },
  currentStockTotalNumber: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '900',
  },
  currentStockTotalLabel: {
    marginTop: 2,
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  noStockBox: {
    backgroundColor: '#faf8f4',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#efe8dd',
  },
  noStockTitle: {
    marginTop: 8,
    color: '#111',
    fontSize: 15,
    fontWeight: '900',
  },
  noStockText: {
    marginTop: 3,
    color: '#777',
    fontSize: 12,
    textAlign: 'center',
  },
  stockTable: {
    gap: 10,
  },
  stockAccordionWrapper: {
    backgroundColor: '#faf8f4',
    borderRadius: 22,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#efe8dd',
  },
  stockTableRow: {
    backgroundColor: '#faf8f4',
    borderRadius: 20,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stockTableCreator: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 12,
  },
  stockTableName: {
    marginLeft: 10,
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
  },
  stockTableMeta: {
    marginLeft: 10,
    marginTop: 2,
    fontSize: 12,
    color: '#777',
    fontWeight: '600',
  },
  stockTableStats: {
    alignItems: 'flex-end',
  },
  stockTableStatText: {
    fontSize: 13,
    fontWeight: '900',
    color: '#111',
  },
  stockChevronRow: {
    marginTop: 2,
    flexDirection: 'row',
    alignItems: 'center',
  },
  stockTableStatMuted: {
    fontSize: 12,
    fontWeight: '700',
    color: '#777',
  },
  stockExpandedBox: {
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#eee7dc',
    padding: 14,
  },
  stockExpandedGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  stockExpandedMetric: {
    backgroundColor: '#faf8f4',
    borderRadius: 16,
    padding: 12,
    minWidth: 140,
    flex: 1,
    borderWidth: 1,
    borderColor: '#efe8dd',
  },
  stockExpandedLabel: {
    fontSize: 10,
    color: '#777',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  stockExpandedValue: {
    fontSize: 13,
    color: '#111',
    fontWeight: '900',
  },
  stockExpandedFooter: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  stockExpandedFooterText: {
    flex: 1,
    color: '#777',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
  },
  stockExpandedButton: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 14,
  },
  stockExpandedButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '900',
  },

  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,17,17,0.38)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 18,
  },
  modalShell: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '92%',
  },
  modalScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: 34,
    padding: 22,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 15 },
    elevation: 8,
  },
  modalTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  modalCreatorIntro: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 12,
  },
  modalCreatorName: {
    marginLeft: 12,
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
  },
  modalCreatorMeta: {
    marginLeft: 12,
    marginTop: 2,
    fontSize: 12,
    fontWeight: '700',
    color: '#777',
  },
  modalCloseButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#f7f5f1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressRow: {
    flexDirection: 'row',
    gap: 7,
    marginBottom: 20,
  },
  progressDot: {
    flex: 1,
    height: 5,
    borderRadius: 999,
    backgroundColor: '#ece7df',
  },
  progressDotActive: {
    backgroundColor: '#111',
  },
  modalStepLabel: {
    fontSize: 12,
    fontWeight: '900',
    color: '#9a8556',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 7,
  },
  modalTitle: {
    fontSize: 25,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.5,
    marginBottom: 7,
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 20,
  },
  modalInputGroup: {
    marginBottom: 16,
  },
  modalInputLabel: {
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
    marginBottom: 4,
  },
  modalHelpText: {
    fontSize: 12,
    color: '#777',
    lineHeight: 17,
    marginBottom: 8,
  },
  inputWithPrefix: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#faf8f4',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#efe8dd',
    paddingHorizontal: 14,
  },
  inputPrefix: {
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
    marginRight: 4,
  },
  inputSuffix: {
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
    marginLeft: 4,
  },
  modalInput: {
    flex: 1,
    paddingVertical: 15,
    fontSize: 16,
    color: '#111',
    fontWeight: '800',
    outlineStyle: 'none',
  },
  bigStockInput: {
    backgroundColor: '#faf8f4',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#efe8dd',
    paddingHorizontal: 18,
    paddingVertical: 20,
    fontSize: 32,
    color: '#111',
    fontWeight: '900',
    textAlign: 'center',
    outlineStyle: 'none',
  },
  stockSuggestionRow: {
    flexDirection: 'row',
    gap: 9,
    marginTop: 4,
  },
  stockSuggestion: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 18,
    backgroundColor: '#faf8f4',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#efe8dd',
  },
  stockSuggestionActive: {
    backgroundColor: '#111',
    borderColor: '#111',
  },
  stockSuggestionText: {
    color: '#111',
    fontSize: 13,
    fontWeight: '900',
  },
  stockSuggestionTextActive: {
    color: '#fff',
  },
  reviewCreatorCard: {
    backgroundColor: '#faf8f4',
    borderRadius: 24,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#efe8dd',
  },
  reviewCreatorName: {
    marginLeft: 12,
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
  },
  reviewCreatorMeta: {
    marginLeft: 12,
    marginTop: 2,
    fontSize: 12,
    color: '#777',
    fontWeight: '700',
  },
  reviewCreatorMetaStrong: {
    marginLeft: 12,
    marginTop: 4,
    fontSize: 12,
    color: '#111',
    fontWeight: '900',
  },
  voucherPreviewLarge: {
    backgroundColor: '#111',
    borderRadius: 26,
    padding: 18,
    marginBottom: 14,
  },
  previewIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  voucherPreviewTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
    letterSpacing: -0.4,
  },
  voucherPreviewText: {
    marginTop: 8,
    color: '#d1d1d1',
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '600',
  },
  reviewGrid: {
    flexDirection: 'row',
    gap: 9,
  },
  reviewBox: {
    flex: 1,
    backgroundColor: '#faf8f4',
    borderRadius: 18,
    padding: 12,
    borderWidth: 1,
    borderColor: '#efe8dd',
  },
  reviewBoxLabel: {
    fontSize: 11,
    fontWeight: '800',
    color: '#777',
  },
  reviewBoxValue: {
    marginTop: 4,
    fontSize: 18,
    fontWeight: '900',
    color: '#111',
  },
  modalButtonRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 22,
  },
  modalSecondaryButton: {
    flex: 1,
    backgroundColor: '#f7f5f1',
    borderRadius: 18,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalSecondaryButtonText: {
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
  },
  modalPrimaryButton: {
    flex: 1.35,
    backgroundColor: '#FDF05D',
    borderRadius: 18,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 7,
  },
  modalPrimaryButtonDisabled: {
    opacity: 0.65,
  },
  modalPrimaryButtonText: {
    color: '#111',
    fontSize: 14,
    fontWeight: '900',
  },

  infoBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,17,17,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 18,
  },
  infoCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#ffffff',
    borderRadius: 32,
    padding: 22,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 15 },
    elevation: 8,
  },
  infoTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  infoIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoCloseButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#f7f5f1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoBadgeText: {
    fontSize: 11,
    color: '#9a8556',
    fontWeight: '900',
    letterSpacing: 1.1,
    marginBottom: 8,
  },
  infoTitle: {
    fontSize: 26,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.5,
    marginBottom: 10,
  },
  infoBody: {
    fontSize: 14,
    color: '#666',
    lineHeight: 21,
    marginBottom: 16,
  },
  infoPoint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#faf8f4',
    borderRadius: 18,
    padding: 13,
    marginBottom: 9,
  },
  infoPointNumber: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#111',
    color: '#fff',
    textAlign: 'center',
    lineHeight: 26,
    fontWeight: '900',
    fontSize: 12,
    marginRight: 10,
    overflow: 'hidden',
  },
  infoPointText: {
    flex: 1,
    color: '#555',
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '600',
  },
  infoPrimaryButton: {
    marginTop: 10,
    backgroundColor: '#111',
    borderRadius: 18,
    paddingVertical: 15,
    alignItems: 'center',
  },
  infoPrimaryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '900',
  },
});